import React, { useState, useEffect } from 'react';

// Component for van selection checkbox
const VanOption = ({ van, isSelected, onToggle, isDisabled }) => {
  return (
    <div className={`
      flex items-center p-3 border rounded-lg mb-2 transition-colors
      ${isSelected ? 'bg-white/30 border-white' : 'bg-gray-800 border-gray-700'}
      ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-700'}
    `}
    onClick={() => !isDisabled && onToggle(van.id)}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => !isDisabled && onToggle(van.id)}
        disabled={isDisabled}
        className="h-5 w-5 text-white border-white rounded focus:ring-white"
      />
      <div className="ml-3 flex-1">
        <div className="flex justify-between">
          <div className="text-white font-medium">{van.name}</div>
          <div className="text-blue-300 text-sm px-2 py-0.5 bg-blue-900/40 rounded-full">
            {van.capacity} seats
          </div>
        </div>
        <div className="text-gray-400 text-sm">{van.license_plate}</div>
      </div>
    </div>
  );
};

const VanAssignmentForm = ({ booking, vans, onSubmit, isLoading, currentAssignments = [] }) => {
  const [selectedVanIds, setSelectedVanIds] = useState([]);
  const [vanCapacity, setVanCapacity] = useState(0);
  const [filteredVans, setFilteredVans] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Initialize the form with current assignments
  useEffect(() => {
    if (currentAssignments && currentAssignments.length > 0) {
      const assignedVanIds = currentAssignments.map(a => a.van_id);
      setSelectedVanIds(assignedVanIds);
    }
  }, [currentAssignments]);
  
  // Filter vans and calculate total capacity when selection changes
  useEffect(() => {
    // Start with all vans
    let vanList = [...vans];
    
    // Calculate total capacity of selected vans
    const totalCapacity = selectedVanIds.reduce((total, vanId) => {
      const van = vans.find(v => v.id === vanId);
      return total + (van ? van.capacity : 0);
    }, 0);
    
    setVanCapacity(totalCapacity);
    setFilteredVans(vanList);
    
    // Check if enough capacity
    if (booking.passengers > 0 && totalCapacity < booking.passengers) {
      setErrorMessage(`Warning: Selected vans only have capacity for ${totalCapacity} passengers, but booking requires ${booking.passengers}`);
    } else {
      setErrorMessage('');
    }
  }, [selectedVanIds, vans, booking]);
  
  const toggleVanSelection = (vanId) => {
    setSelectedVanIds(prev => {
      if (prev.includes(vanId)) {
        return prev.filter(id => id !== vanId);
      } else {
        return [...prev, vanId];
      }
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log("🎯 VanAssignmentForm: Form submitted!");
    console.log("Selected van IDs:", selectedVanIds);
    console.log("Booking object:", booking);
    console.log("Booking ID:", booking.id, "Type:", typeof booking.id);
    console.log("Van capacity:", vanCapacity);
    console.log("Required passengers:", booking.passengers);
    
    if (selectedVanIds.length === 0) {
      console.log("❌ No vans selected");
      setErrorMessage('Please select at least one van');
      return;
    }
    
    if (booking.passengers > vanCapacity) {
      console.log("⚠️ Insufficient capacity, showing confirmation");
      // Confirm with the user
      if (!window.confirm(`Warning: Selected vans only have capacity for ${vanCapacity} passengers, but booking requires ${booking.passengers}. Continue anyway?`)) {
        console.log("❌ User cancelled due to capacity warning");
        return;
      }
    }
    
    console.log("✅ Calling onSubmit with van IDs:", selectedVanIds);
    onSubmit(selectedVanIds);
  };
  
  // Calculate how many vans are needed based on passenger count
  const vansNeeded = Math.ceil(booking.passengers / (vans.length > 0 ? vans[0].capacity : 7));
  
  return (
    <form onSubmit={handleSubmit}>
      <h4 className="text-white font-medium mb-2">Select Vans</h4>
      
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-white">Passengers: {booking.passengers}</span>
          <span className="text-gray-400">|</span>
          <span className="text-white">Current capacity: {vanCapacity}</span>
          <span className="text-gray-400">|</span>
          <span className="text-white">Vans needed: {vansNeeded}</span>
        </div>
        
        {vansNeeded > 1 && (
          <div className="bg-white/20 border border-white text-white px-3 py-2 rounded-md mb-4">
            <span className="font-medium">Note:</span> This booking requires {vansNeeded} vans based on passenger count.
          </div>
        )}
        
        {errorMessage && (
          <div className="bg-red-900/20 border border-red-800 text-red-200 px-3 py-2 rounded-md mb-4">
            {errorMessage}
          </div>
        )}
      </div>
      
      <div className="mb-6 max-h-64 overflow-y-auto pr-1">
        {filteredVans.length > 0 ? (
          filteredVans.map(van => (
            <VanOption
              key={van.id}
              van={van}
              isSelected={selectedVanIds.includes(van.id)}
              onToggle={toggleVanSelection}
              isDisabled={isLoading}
            />
          ))
        ) : (
          <p className="text-gray-400 text-center py-4">No vans available</p>
        )}
      </div>
      
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={() => setSelectedVanIds([])}
          className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-800 transition-colors"
          disabled={isLoading || selectedVanIds.length === 0}
        >
          Clear Selection
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-white hover:bg-gray-200 text-black rounded-md transition-colors"
          disabled={isLoading || selectedVanIds.length === 0}
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Assigning...
            </span>
          ) : (
            'Assign Vans'
          )}
        </button>
      </div>
    </form>
  );
};

export default VanAssignmentForm; 